/**
 * HubSpot integration utilities
 * Handles form submissions and tracking script loading
 */

import { supabase } from '@/integrations/supabase/client';

interface HubSpotFormData {
  fields: Array<{ name: string; value: string | string[] }>; // Support both single values and arrays for multi-select
  context?: {
    pageUri?: string;
    pageName?: string;
    hutk?: string;        // hubspotutk cookie value (for contact linking)
    ipAddress?: string;    // User's IP address (for analytics)
  };
}

interface HubSpotConfig {
  portalId: string;
  formId: string;
}

/**
 * Get HubSpot configuration from environment variables
 */
export function getHubSpotConfig(): HubSpotConfig | null {
  const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID;
  const formId = import.meta.env.VITE_HUBSPOT_FORM_ID;

  if (!portalId || !formId) {
    if (import.meta.env.DEV) {
      console.warn('⚠️ HubSpot not configured. Set VITE_HUBSPOT_PORTAL_ID and VITE_HUBSPOT_FORM_ID in your .env file');
    }
    return null;
  }

  return { portalId, formId };
}

/**
 * Submit form data to HubSpot Forms API
 * Supports both single values and arrays for multi-select fields
 */
export async function submitToHubSpot(formData: HubSpotFormData): Promise<{ success: boolean; error?: string; response?: unknown }> {
  const config = getHubSpotConfig();
  
  if (!config) {
    return {
      success: false,
      error: 'HubSpot not configured. Please set VITE_HUBSPOT_PORTAL_ID and VITE_HUBSPOT_FORM_ID',
    };
  }

  // Format fields for HubSpot API
  // Multi-select fields: send as semicolon-separated string (HubSpot requirement)
  // Single-select fields: send single entry with string value
  const formattedFields: Array<{ name: string; value: string }> = [];
  
  formData.fields.forEach(field => {
    if (Array.isArray(field.value)) {
      // Multi-select: join values with semicolon (HubSpot format)
      formattedFields.push({
        name: field.name,
        value: field.value.join(';'),
      });
    } else {
      // Single-select: single field entry
      formattedFields.push({
        name: field.name,
        value: String(field.value),
      });
    }
  });

  // Build payload - context includes hutk (hubspotutk cookie) and ipAddress
  const payload: {
    fields: Array<{ name: string; value: string }>;
    context?: HubSpotFormData['context'];
  } = {
    fields: formattedFields,
  };
  
  // Add context if provided (includes hutk and ipAddress)
  if (formData.context) {
    payload.context = formData.context;
  }

  try {
    if (import.meta.env.DEV) {
      console.log('📤 Submitting to HubSpot:', {
        portalId: config.portalId,
        formId: config.formId,
        fields: formattedFields,
        fullPayload: payload,
      });
      console.log('📋 Payload JSON:', JSON.stringify(payload, null, 2));
      
      // Log name fields specifically for debugging
      const nameFields = formattedFields.filter(f => f.name === 'firstname' || f.name === 'lastname');
      if (nameFields.length > 0) {
        console.log('👤 Name fields being sent:', nameFields);
      }
    }

    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${config.portalId}/${config.formId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    const responseText = await response.text();
    let result;
    
    try {
      result = JSON.parse(responseText);
    } catch {
      result = { message: responseText };
    }

    if (!response.ok) {
      const errorMessage = result.message || result.errors?.[0]?.message || result.error || `HubSpot API error: ${response.status} ${response.statusText}`;
      
      if (import.meta.env.DEV) {
        console.error('❌ HubSpot API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: result,
          errorDetails: result.errors || result.invalidFields || result.message,
          portalId: config.portalId,
          formId: config.formId,
          payload: payload,
        });
        
        // Log the full error response for debugging
        console.error('📋 Full Error Response:', JSON.stringify(result, null, 2));
        
        // Specific error messages for common issues
        if (response.status === 404) {
          console.error('🔍 404 Error - Possible issues:');
          console.error('   - Portal ID might be wrong. Check: Account & Billing → Account Defaults');
          console.error('   - Form ID might be wrong. Check: Marketing → Forms → [Your Form] → Form ID');
          console.error('   - Form might not exist or be deleted');
          console.error(`   Current Portal ID: ${config.portalId}`);
          console.error(`   Current Form ID: ${config.formId}`);
        }
        if (response.status === 400) {
          console.error('🔍 400 Error - Possible issues:');
          console.error('   - Field names don\'t match HubSpot form fields');
          console.error('   - Required fields missing');
          console.error('   - Field value format incorrect');
          console.error('   - Field values don\'t match allowed options');
          console.error('   Check HubSpot form field names: Settings → Properties → Contact properties');
          console.error('   Check HubSpot form: Marketing → Forms → [Your Form] → Verify fields match');
          
          // Show which fields we're sending
          console.error('📤 Fields being sent:', formattedFields.map(f => `${f.name}: ${f.value}`).join(', '));
          
          // Show full error response if available
          if (result.errors || result.invalidFields) {
            console.error('📋 HubSpot Error Details:', JSON.stringify(result.errors || result.invalidFields, null, 2));
          }
          
          // Show which fields we're sending
          console.error('📤 Fields being sent:', formattedFields.map(f => `${f.name}: ${f.value}`).join(', '));
        }
      }
      
      throw new Error(errorMessage);
    }
    
    if (import.meta.env.DEV) {
      console.log('✅ HubSpot submission successful:', result);
    }

    // Return success with response data (may contain contact ID)
    return { success: true, response: result };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error submitting to HubSpot';
    
    // Check if error is due to tracking prevention (ERR_BLOCKED_BY_CLIENT)
    const isBlocked = errorMessage.includes('Failed to fetch') || 
                      errorMessage.includes('blocked') ||
                      errorMessage.includes('ERR_BLOCKED_BY_CLIENT') ||
                      (error instanceof TypeError && errorMessage.includes('fetch'));
    
    if (import.meta.env.DEV) {
      if (isBlocked) {
        console.warn('⚠️ HubSpot submission blocked by browser tracking prevention. This is normal in development with privacy features enabled.');
        console.warn('💡 Tip: Disable tracking prevention or ad blockers to test HubSpot integration in development.');
        console.warn('💡 Alternative: Test in production/staging environment where tracking prevention is less likely.');
      } else {
        console.error('❌ HubSpot submission failed:', errorMessage);
        console.error('Full error:', error);
      }
    }

    return {
      success: false,
      error: isBlocked 
        ? 'Submission blocked by browser privacy settings. This won\'t affect production users.'
        : errorMessage,
    };
  }
}

/**
 * Load HubSpot tracking script
 * This enables HubSpot tracking cookies and analytics
 */
export function loadHubSpotTracking(portalId: string): void {
  if (typeof window === 'undefined') return;

  // Check if script is already loaded
  if (document.getElementById('hs-script-loader')) {
    return;
  }

  // Create and append HubSpot tracking script
  const script = document.createElement('script');
  script.id = 'hs-script-loader';
  script.type = 'text/javascript';
  script.src = `//js.hs-scripts.com/${portalId}.js`;
  script.async = true;
  script.defer = true;
  
  // Handle script loading errors (e.g., blocked by tracking prevention)
  script.onerror = () => {
    if (import.meta.env.DEV) {
      console.warn('⚠️ HubSpot tracking script blocked by browser tracking prevention. This is normal in development with privacy features enabled.');
    }
  };
  
  script.onload = () => {
    if (import.meta.env.DEV) {
      console.log('📊 HubSpot tracking script loaded');
    }
  };
  
  document.body.appendChild(script);
}

/**
 * Get HubSpot tracking cookie (hubspotutk)
 */
export function getHubSpotCookie(): string {
  if (typeof document === 'undefined') return '';
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; hubspotutk=`);
  
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || '';
  }
  
  return '';
}

/**
 * Get client IP address using ipify service
 * Returns empty string if fetch fails or times out (graceful degradation)
 */
export async function getClientIP(): Promise<string> {
  if (typeof window === 'undefined') return '';
  
  try {
    // Use AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
    
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      return '';
    }
    
    const data = await response.json();
    return data.ip || '';
  } catch (error) {
    // Silently fail - IP address is optional for form submissions
    if (import.meta.env.DEV) {
      console.debug('⚠️ Could not fetch IP address:', error instanceof Error ? error.message : 'Unknown error');
    }
    return '';
  }
}

/**
 * Update HubSpot Contact explicitly to ensure correct firstname and lastname
 * This fixes issues where HubSpot form field mappings might cause incorrect values
 */
export async function updateHubSpotContact(
  email: string,
  firstName: string,
  lastName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('update-hubspot-contact', {
      body: {
        email,
        firstName,
        lastName: lastName || '',
      },
    });

    if (error) {
      console.error('Error calling update-hubspot-contact Edge Function:', error);
      return {
        success: false,
        error: error.message || 'Failed to update HubSpot contact',
      };
    }

    if (data?.success) {
      if (import.meta.env.DEV) {
        console.log('✅ HubSpot contact updated:', { email, firstName, lastName });
      }
      return { success: true };
    } else {
      return {
        success: false,
        error: data?.error || 'Unknown error updating HubSpot contact',
      };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error updating HubSpot contact';
    console.error('Error updating HubSpot contact:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Initialize HubSpot tracking (call this on app load)
 */
export function initHubSpotTracking(): void {
  const config = getHubSpotConfig();
  
  if (config) {
    loadHubSpotTracking(config.portalId);
  }
}

/**
 * Get HubSpot meeting URL from environment variables
 * Returns the full meeting URL or null if not configured
 */
export function getHubSpotMeetingUrl(): string | null {
  const meetingLink = import.meta.env.VITE_HUBSPOT_MEETING_LINK;

  if (!meetingLink) {
    if (import.meta.env.DEV) {
      console.warn('⚠️ HubSpot meeting link not configured. Set VITE_HUBSPOT_MEETING_LINK in your .env file');
    }
    return null;
  }

  // If the meeting link already includes the full URL, return it as-is
  if (meetingLink.startsWith('http://') || meetingLink.startsWith('https://')) {
    return meetingLink;
  }

  // Otherwise, construct the full HubSpot Meetings URL
  return `https://meetings.hubspot.com/${meetingLink}`;
}

